import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Loader2, 
  CheckCircle, 
  X, 
  ChevronRight, 
  PartyPopper, 
  User, 
  Ghost 
} from 'lucide-react';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { db, auth } from '../firebase'; 
import appStoreIcon from '../assets/app-store.png';
import { ParticlesBackground } from './ParticlesBackground';
import MagneticButton from './MagneticButton';

export const Hero = () => {
  const navigate = useNavigate();
  // --- RESTORED: iOS Interest & Firebase State ---
  const [showIosModal, setShowIosModal] = useState(false);
  const [iosEmail, setIosEmail] = useState('');
  const [validationError, setValidationError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('idle'); // idle, success, exists, error

  // --- RESTORED: Lock body scroll logic ---
  useEffect(() => {
    if (showIosModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => { document.body.style.overflow = 'unset'; };
  }, [showIosModal]);

  // --- RESTORED: Handle Modal Close ---
  const handleClose = () => {
    setShowIosModal(false);
    setTimeout(() => {
      setSubmitStatus('idle');
      setIosEmail('');
      setValidationError('');
    }, 300);
  };

  // --- RESTORED: Handle Firebase Submit ---
  const handleIosSubmit = async (e) => {
    e.preventDefault();
    setValidationError('');

    if (!iosEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(iosEmail)) {
      setValidationError("Please enter a valid email address."); 
      return;
    }

    setIsSubmitting(true);
    
    try {
      const docRef = doc(db, 'interests', iosEmail);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        setSubmitStatus('exists');
      } else {
        await setDoc(docRef, {
          timestamp: new Date().toISOString()
        });
        setSubmitStatus('success');
      }
      
    } catch (error) {
      // console.error("Error registering interest:", error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStartExploringClick = async (e) => {
    e.preventDefault();
    const user = auth.currentUser;
    if (user) {
        try {
            const userDoc = await getDoc(doc(db, 'users', user.uid));
            if (userDoc.exists() && userDoc.data().isSetupComplete) {
                navigate('/');
                return;
            }
        } catch (error) {
            // console.error("Error checking user setup status:", error);
        }
    }
    navigate('/login');
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
      <ParticlesBackground />
      {/* --- RESTORED: Full iOS Modal with all Logic States --- */}
      {createPortal(
        <AnimatePresence>
          {showIosModal && (
            <motion.div 
              className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-sp-bg/60 backdrop-blur-sm"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={handleClose}
            >
              <motion.div 
                className="bg-sp-card-bg border border-sp-border p-8 rounded-3xl w-full max-w-md relative shadow-[0_0_40px_var(--sp-glow)]"
                initial={{ scale: 0.9, opacity: 0, y: 20 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ scale: 0.9, opacity: 0, y: 20 }}
                onClick={e => e.stopPropagation()}
              >
                <button onClick={handleClose} className="absolute top-4 right-4 text-sp-text-secondary hover:text-sp-text">
                  <X size={24} />
                </button>

                {submitStatus === 'success' && (
                  <div className="text-center py-6">
                    <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="inline-block mb-4">
                      <CheckCircle className="w-16 h-16 text-sp-cyan mx-auto" />
                    </motion.div>
                    <h3 className="text-2xl font-bold text-sp-text mb-2">You're on the list!</h3>
                    <p className="text-sp-text-secondary mb-6">We'll notify you the second we launch on iOS.</p>
                    <button onClick={handleClose} className="w-full bg-sp-cyan-dim hover:bg-sp-cyan/20 text-sp-cyan font-semibold py-3 rounded-xl transition-all">Got it</button>
                  </div>
                )}

                {submitStatus === 'exists' && (
                  <div className="text-center py-6">
                    <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="inline-block mb-4">
                      <PartyPopper className="w-16 h-16 text-sp-violet mx-auto" />
                    </motion.div>
                    <h3 className="text-2xl font-bold text-sp-text mb-2">You're already in!</h3>
                    <p className="text-sp-text-secondary mb-6">We've got your email. Stay tuned!</p>
                    <button onClick={handleClose} className="w-full bg-sp-violet/10 hover:bg-sp-violet/20 text-sp-violet font-semibold py-3 rounded-xl transition-all">Awesome</button>
                  </div>
                )}

                {(submitStatus === 'idle' || submitStatus === 'error') && (
                  <>
                    <h3 className="text-2xl font-bold text-sp-text mb-2">Spark for iOS</h3>
                    <p className="text-sp-text-secondary mb-6">Drop your email to get early access when we launch on the App Store.</p>
                    <form onSubmit={handleIosSubmit} className="space-y-4">
                      <div>
                        <input
                          type="email" placeholder="Enter your email" value={iosEmail}
                          onChange={(e) => { setIosEmail(e.target.value); if(validationError) setValidationError(''); }}
                          className={`w-full bg-sp-bg/40 border ${validationError ? 'border-sp-cyan' : 'border-sp-border'} text-sp-text p-4 rounded-xl focus:outline-none focus:border-sp-cyan`}
                          disabled={isSubmitting}
                        />
                        {validationError && (
                          <motion.p initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }} className="text-sp-cyan text-sm mt-2 pl-1">
                            {validationError}
                          </motion.p>
                        )}
                      </div>
                      <button type="submit" disabled={isSubmitting} className="w-full bg-sp-cyan hover:bg-sp-cyan/80 text-black font-bold py-4 rounded-xl transition-all flex items-center justify-center gap-2">
                        {isSubmitting ? <Loader2 className="animate-spin w-5 h-5" /> : <><span>Notify Me</span><ChevronRight className="w-5 h-5" /></>}
                      </button>
                      {submitStatus === 'error' && (
                        <p className="text-sp-violet text-sm text-center">Something went wrong. Please try again.</p>
                      )}
                    </form>
                  </>
                )}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}

      <div className="relative z-10 text-center px-4 max-w-4xl mx-auto">
        
        {/* --- LOCKED: HORIZONTAL ORBITING IDENTITY SYSTEM --- */}
        <div className="relative h-32 flex items-center justify-center mb-12">
          
          {/* REALITY MODE Identity */}
          <motion.div
            className="absolute"
            animate={{
              x: [-160, 160, -160],
              scale: [0.7, 1.2, 0.7],
              opacity: [0.4, 1, 0.4],
              zIndex: [10, 30, 10]
            }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
          >
            <User className="w-14 h-14 text-sp-text drop-shadow-[0_0_15px_rgba(255,255,255,0.3)]" />
          </motion.div>

          {/* SHADOW MODE Identity (Mask/Ghost) */}
          <motion.div
            className="absolute"
            animate={{
              x: [160, -160, 160],
              scale: [0.7, 1.2, 0.7],
              opacity: [0.4, 1, 0.4],
              zIndex: [10, 30, 10]
            }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
          >
            <Ghost className="w-16 h-16 text-sp-cyan drop-shadow-[0_0_20px_var(--sp-glow)]" />
          </motion.div>
        </div>

          <motion.h1
            className="text-5xl md:text-7xl lg:text-[7.5rem] font-medium text-sp-text mb-6 leading-[0.95] tracking-tighter flex flex-col items-center justify-center"
            initial="hidden"
            animate="visible"
            variants={{
              hidden: { opacity: 0 },
              visible: { opacity: 1, transition: { staggerChildren: 0.15 } }
            }}
          >
            <motion.span 
              variants={{ 
                hidden: { opacity: 0, y: 40, filter: 'blur(10px)' }, 
                visible: { opacity: 1, y: 0, filter: 'blur(0px)', transition: { duration: 1.2, ease: [0.16, 1, 0.3, 1] } } 
              }} 
              className="block"
            >
              The <span className="text-sp-cyan drop-shadow-[0_0_20px_rgba(11,217,229,0.3)]">Dual-Identity</span>
            </motion.span>
            <motion.span 
              variants={{ 
                hidden: { opacity: 0, y: 40, filter: 'blur(10px)' }, 
                visible: { opacity: 1, y: 0, filter: 'blur(0px)', transition: { duration: 1.2, ease: [0.16, 1, 0.3, 1] } } 
              }} 
              className="block font-light text-4xl md:text-6xl lg:text-[6rem] opacity-90 mt-2"
            >
              Campus Social Network
            </motion.span>
          </motion.h1>
  
          <motion.p
            className="text-lg md:text-xl text-sp-text-secondary mb-12 max-w-2xl mx-auto font-light leading-relaxed"
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
          >
            Switch between <span className="text-sp-text font-medium">Reality Mode</span> for the vibe and <span className="text-sp-cyan font-medium">Shadow Mode</span> for the secrets. One App. Two Identities. Zero Judgment.
          </motion.p>
  
          {/*
          <motion.div className="flex flex-col sm:flex-row gap-8 justify-center items-center mb-8" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
            <motion.a href="https://play.google.com/store/apps/details?id=com.sparkthegenzapp" target="_blank" whileHover={{ scale: 1.05 }} className="flex flex-col items-center">
              <img src={playStoreIcon} alt="Play Store" className="h-12 w-auto shadow-[0_0_30px_var(--sp-glow)] rounded-xl" />
            </motion.a>

            <motion.button onClick={() => setShowIosModal(true)} whileHover={{ scale: 1.05 }} className="flex flex-col items-center group cursor-pointer bg-transparent border-none">
              <img src={appStoreIcon} alt="App Store" className="h-12 w-auto opacity-70 group-hover:opacity-100 rounded-xl" />
              <p className="text-sp-cyan text-sm mt-2 font-medium">Coming Soon <span className="opacity-60">(Notify Me)</span></p>
            </motion.button>
          </motion.div>
          */}

          {/* Start Exploring - Web App CTA */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={{ delay: 0.8 }}
            className="flex justify-center mt-4"
          >
            <MagneticButton>
              <button onClick={handleStartExploringClick} className="relative group overflow-hidden rounded-full font-semibold transition-all duration-300 shadow-[0_0_30px_var(--sp-glow)] hover:scale-105 hover:shadow-[0_0_50px_var(--sp-cyan-dim)]">
                <div className="absolute inset-0 fluid-btn-bg opacity-10 group-hover:opacity-100 transition-opacity duration-500"></div>
                <div className="relative flex items-center gap-2 px-8 py-4 border border-sp-cyan/30 group-hover:border-transparent rounded-full z-10 transition-colors duration-300">
                  <span className="text-sp-text tracking-wide group-hover:text-black transition-colors duration-300">Sign In</span>
                  <ChevronRight className="w-5 h-5 text-sp-cyan group-hover:text-black group-hover:translate-x-1 transition-all duration-300" />
                </div>
              </button>
            </MagneticButton>
          </motion.div>
      </div>
    </section>
  );
};




export default Hero;
